/**
 * Upsell Popup Module - Ultimate Trance Theme
 * Handles upsell popup display, bundle/single product selection
 */

/* Locale-aware root URL for Shopify Markets */
const _upsellRootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const _upsellT = (window.theme && window.theme.strings) || {};

const UpsellPopup = {
    popup: null,
    overlay: null,
    closeBtn: null,
    bundleBtn: null,
    singleBtn: null,
    config: null,
    singleProduct: null,
    isOpen: false,
    savedBundleBtnNodes: null,
    savedSingleBtnNodes: null,
    _confettiFrame: null,
    _confettiCanvas: null,

    /**
     * Initialize the upsell popup module
     */
    init() {
        this.popup = document.querySelector('.upsell-popup');
        this.overlay = document.querySelector('.upsell-overlay');

        // Always register the trigger interceptor (even if popup HTML is missing)
        this.bindTriggerHandler();

        if (!this.popup || !this.overlay) return;

        this.closeBtn = this.popup.querySelector('.upsell-close');
        this.bundleBtn = this.popup.querySelector('[data-upsell-bundle]');
        this.singleBtn = this.popup.querySelector('[data-upsell-single]');

        // Save original button content for restoration
        if (this.bundleBtn) {
            this.savedBundleBtnNodes = Array.prototype.slice.call(this.bundleBtn.childNodes).map(function(node) {
                return node.cloneNode(true);
            });
        }
        if (this.singleBtn) {
            this.savedSingleBtnNodes = Array.prototype.slice.call(this.singleBtn.childNodes).map(function(node) {
                return node.cloneNode(true);
            });
        }

        // Parse configuration from section data
        const configScript = document.getElementById('upsell-popup-data');
        if (configScript) {
            try {
                this.config = JSON.parse(configScript.textContent);
            } catch (e) {
                console.warn('Could not parse upsell popup config:', e);
                return;
            }
        }

        // Check if disabled via config OR URL parameter
        if (!this.config || !this.config.enabled) return;
        if (window.themeUtils && !window.themeUtils.shouldShowUpsell()) return;

        this.bindPopupEvents();
        this.hydrateFromConfig();
    },

    /**
     * Hydrate single product data from config
     */
    hydrateFromConfig() {
        // Use single product from section config if available
        if (this.config && this.config.single && this.config.single.variant_id) {
            this.singleProduct = this.config.single;
        }
    },

    /**
     * Bind the delegated trigger handler — runs once, regardless of popup state
     */
    bindTriggerHandler() {
        if (this._triggerBound) return;
        this._triggerBound = true;

        document.addEventListener('click', (e) => {
            const trigger = e.target.closest('[data-trigger-upsell]');
            if (!trigger) return;

            // Capture the original href so we can fall back if popup can't open
            const fallbackHref = trigger.tagName === 'A' ? trigger.getAttribute('href') : null;

            // Always prevent default first for <a> tags to stop immediate navigation
            if (fallbackHref) {
                e.preventDefault();
                e.stopPropagation();
            }

            // Helper to fall back to original navigation when popup can't open
            const fallback = () => {
                if (fallbackHref) {
                    window.location.href = fallbackHref;
                }
            };

            // Check if upsell should show (respects hide-upsell param)
            if (window.themeUtils && !window.themeUtils.shouldShowUpsell()) { fallback(); return; }

            // Check if direct-checkout mode should skip popup entirely
            if (window.themeUtils && window.themeUtils.isSkipPopupMode()) { fallback(); return; }

            // Check if upsell is enabled and configured with bundle product
            if (!this.config || !this.config.enabled) { fallback(); return; }
            if (!this.config.bundle || !this.config.bundle.variant_id) { fallback(); return; }

            // Only intercept if we have single product configured
            if (!this.config.single || !this.config.single.variant_id) { fallback(); return; }

            // For non-<a> triggers, prevent default now
            if (!fallbackHref) {
                e.preventDefault();
                e.stopPropagation();
            }

            if (window.themeAnalytics) {
                window.themeAnalytics.capture('upsell_popup_opened', {
                    trigger_text: (trigger.textContent || '').replace(/\s+/g, ' ').trim(),
                    trigger_product: trigger.getAttribute('data-ph-capture-attribute-product') || trigger.dataset.productTitle || '',
                    trigger_variant_id: trigger.dataset.variantId || ''
                });
            }
            this.open();
        });
    },

    /**
     * Bind popup-internal event listeners (close, bundle, single buttons)
     */
    bindPopupEvents() {
        // Store bound handlers for cleanup
        this._handlers = {};

        this._handlers.close = () => this.close();
        this._handlers.escape = (e) => { if (e.key === 'Escape' && this.isOpen) this.close(); };
        this._handlers.bundle = (e) => this.handleBundleClick(e);
        this._handlers.single = (e) => this.handleSingleClick(e);

        if (this.closeBtn) this.closeBtn.addEventListener('click', this._handlers.close);
        this.overlay.addEventListener('click', this._handlers.close);
        document.addEventListener('keydown', this._handlers.escape);
        if (this.bundleBtn) this.bundleBtn.addEventListener('click', this._handlers.bundle);
        if (this.singleBtn) this.singleBtn.addEventListener('click', this._handlers.single);
    },

    /**
     * Remove popup event listeners to prevent leaks on re-init
     */
    destroyPopupEvents() {
        // Always stop confetti on teardown, even if _handlers was already cleared.
        // Safety belt for theme-editor section reload during an active confetti animation.
        this._stopConfetti();

        if (!this._handlers) return;
        if (this.closeBtn) this.closeBtn.removeEventListener('click', this._handlers.close);
        if (this.overlay) this.overlay.removeEventListener('click', this._handlers.close);
        document.removeEventListener('keydown', this._handlers.escape);
        if (this.bundleBtn) this.bundleBtn.removeEventListener('click', this._handlers.bundle);
        if (this.singleBtn) this.singleBtn.removeEventListener('click', this._handlers.single);
        this._handlers = null;
    },

    /**
     * Open the popup
     */
    open() {
        if (!this.popup || !this.overlay) return;

        // Clear any in-progress close animation (including the pending cleanup
        // timer, which would otherwise hide a popup reopened within 300ms and
        // leave the page scroll-locked behind an invisible overlay)
        if (this._closeCleanupTimer) {
            clearTimeout(this._closeCleanupTimer);
            this._closeCleanupTimer = null;
        }
        this.popup.classList.remove('closing');
        this.overlay.classList.remove('closing');

        // Save scroll position before anything changes
        const scrollX = window.scrollX;
        const scrollY = window.scrollY;

        this.popup.classList.add('open');
        this.overlay.classList.add('open');
        this.popup.setAttribute('aria-hidden', 'false');
        this.overlay.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
        this.isOpen = true;

        // Restore scroll position
        window.scrollTo(scrollX, scrollY);

        // Fire confetti
        this.fireConfetti();

        // Trap Tab inside the dialog; released (with focus restore) on close
        if (!this._releaseTrap && window.themeUtils && window.themeUtils.trapFocus) {
            this._releaseTrap = window.themeUtils.trapFocus(this.popup);
        }

        // Focus close button for accessibility
        if (this.closeBtn) {
            setTimeout(() => this.closeBtn.focus({ preventScroll: true }), 100);
        }
    },

    /**
     * Stop any in-flight confetti animation and remove its canvas.
     * Safe to call when no confetti is running (idempotent null-checks).
     */
    _stopConfetti() {
        if (this._confettiFrame) {
            cancelAnimationFrame(this._confettiFrame);
            this._confettiFrame = null;
        }
        if (this._confettiCanvas) {
            this._confettiCanvas.remove();
            this._confettiCanvas = null;
        }
    },

    /**
     * Fire confetti burst from the popup area
     */
    fireConfetti() {
        // Respect OS-level reduced motion — CSS can't gate canvas animation
        if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

        // Cancel any prior confetti (rapid re-open in editor or multi-fire paths)
        this._stopConfetti();

        const isMobile = window.innerWidth <= 768;
        const dpr = isMobile ? 1 : (window.devicePixelRatio || 1);
        const w = window.innerWidth;
        const h = window.innerHeight;

        const canvas = document.createElement('canvas');
        canvas.style.cssText = 'position:fixed;inset:0;width:100%;height:100%;pointer-events:none;z-index:1022;';
        canvas.width = w * dpr;
        canvas.height = h * dpr;
        document.body.appendChild(canvas);
        this._confettiCanvas = canvas;

        const ctx = canvas.getContext('2d');
        if (dpr !== 1) ctx.scale(dpr, dpr);

        const colors = ['#E3FC02', '#FFD700', '#FF6B6B', '#4ECDC4', '#A78BFA', '#FB923C', '#34D399', '#F472B6', '#FBBF24', '#ffffff'];
        const particles = [];
        const count = isMobile ? 40 : 150;
        const cx = w / 2;
        const cy = h * 0.4;

        for (let i = 0; i < count; i++) {
            const angle = (Math.random() * Math.PI * 2);
            const speed = 3 + Math.random() * 9;
            particles.push({
                x: cx + (Math.random() - 0.5) * 60,
                y: cy + (Math.random() - 0.5) * 40,
                vx: Math.cos(angle) * speed * (0.5 + Math.random() * 0.9),
                vy: Math.sin(angle) * speed * -1.2 + (Math.random() * -2),
                w: 4 + Math.random() * 7,
                h: 3 + Math.random() * 5,
                color: colors[Math.floor(Math.random() * colors.length)],
                rotation: isMobile ? 0 : Math.random() * 360,
                rotationSpeed: isMobile ? 0 : (Math.random() - 0.5) * 10,
                gravity: 0.06 + Math.random() * 0.05,
                opacity: 1,
                decay: isMobile ? 0.008 + Math.random() * 0.006 : 0.003 + Math.random() * 0.004
            });
        }

        const animate = () => {
            ctx.clearRect(0, 0, w, h);
            let alive = false;

            if (isMobile) {
                // Mobile: no rotation, no save/restore, just rects
                for (const p of particles) {
                    if (p.opacity <= 0) continue;
                    alive = true;
                    p.x += p.vx;
                    p.y += p.vy;
                    p.vy += p.gravity;
                    p.vx *= 0.99;
                    p.opacity -= p.decay;

                    ctx.globalAlpha = Math.max(0, p.opacity);
                    ctx.fillStyle = p.color;
                    ctx.fillRect(p.x - p.w * 0.5, p.y - p.h * 0.5, p.w, p.h);
                }
            } else {
                // Desktop: full rotation + mixed shapes
                for (const p of particles) {
                    if (p.opacity <= 0) continue;
                    alive = true;
                    p.x += p.vx;
                    p.y += p.vy;
                    p.vy += p.gravity;
                    p.vx *= 0.99;
                    p.rotation += p.rotationSpeed;
                    p.opacity -= p.decay;

                    ctx.save();
                    ctx.globalAlpha = Math.max(0, p.opacity);
                    ctx.translate(p.x, p.y);
                    ctx.rotate(p.rotation * Math.PI / 180);
                    ctx.fillStyle = p.color;

                    if (p.w > p.h) {
                        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
                    } else {
                        ctx.beginPath();
                        ctx.arc(0, 0, p.w / 2, 0, Math.PI * 2);
                        ctx.fill();
                    }
                    ctx.restore();
                }
            }

            ctx.globalAlpha = 1;

            if (alive) {
                this._confettiFrame = requestAnimationFrame(animate);
            } else {
                // Natural decay — all particles done. Clean up canvas + frame ref.
                this._confettiFrame = null;
                if (this._confettiCanvas === canvas) {
                    canvas.remove();
                    this._confettiCanvas = null;
                }
            }
        };

        this._confettiFrame = requestAnimationFrame(animate);
    },

    /**
     * Close the popup with exit animation
     */
    close() {
        if (!this.popup || !this.overlay) return;

        this.isOpen = false;
        document.body.style.overflow = '';

        // Release the focus trap and restore focus to the triggering element
        if (this._releaseTrap) {
            this._releaseTrap();
            this._releaseTrap = null;
        }

        // Kill any in-flight confetti — prevents canvas + rAF leak when user
        // dismisses the popup before particles naturally decay (~10s).
        this._stopConfetti();

        // Start close animation
        this.popup.classList.add('closing');
        this.overlay.classList.add('closing');

        // Wait for animation to finish, then clean up
        const cleanup = () => {
            this.popup.removeEventListener('animationend', cleanup);
            this._closeCleanupTimer = null;
            // A reopen raced the close animation — leave the popup visible
            if (this.isOpen) return;
            this.popup.classList.remove('open', 'closing');
            this.overlay.classList.remove('open', 'closing');
            this.popup.setAttribute('aria-hidden', 'true');
            this.overlay.setAttribute('aria-hidden', 'true');
        };

        this.popup.addEventListener('animationend', cleanup, { once: true });

        // Safety fallback in case animationend doesn't fire
        this._closeCleanupTimer = setTimeout(cleanup, 300);
    },

    /**
     * Handle bundle button click
     */
    handleBundleClick(e) {
        const btn = e.currentTarget;
        const variantId = btn.dataset.variantId || this.config.bundle.variant_id;
        const productHandle = this.config.bundle?.handle;

        if (!variantId) return;

        // Disable immediately so rapid taps can't double-fire, regardless of which
        // downstream branch runs (direct_checkout or add-to-cart).
        btn.disabled = true;

        // Check if direct checkout mode is enabled via URL param (show_popup_checkout mode)
        if (window.themeUtils && window.themeUtils.shouldDirectCheckout()) {
            // Stop propagation so the delegated [data-action="direct_checkout"] handler
            // on document doesn't also fire redirectToCheckout for this same click.
            e.preventDefault();
            e.stopPropagation();
            this.addToCartAndCheckout(variantId, btn, this.savedBundleBtnNodes, productHandle);
            return;
        }

        // Direct checkout action — add to cart via AJAX then redirect to checkout
        if (btn.dataset.action === 'direct_checkout') {
            e.preventDefault();
            e.stopPropagation();
            this.addToCartAndCheckout(variantId, btn, this.savedBundleBtnNodes, productHandle);
            return;
        }

        // Add to cart
        this.addToCart(variantId, btn, this.savedBundleBtnNodes, productHandle);
    },

    /**
     * Handle single product button click
     */
    handleSingleClick(e) {
        const btn = e.currentTarget;
        const variantId = btn.dataset.variantId || (this.singleProduct && this.singleProduct.variant_id);

        if (!variantId) {
            console.warn('No single product variant ID available');
            this.close();
            return;
        }

        // Disable immediately so rapid taps can't double-fire.
        btn.disabled = true;

        const productHandle = this.singleProduct ? this.singleProduct.handle : null;

        // Check if direct checkout mode is enabled via URL param (show_popup_checkout mode)
        if (window.themeUtils && window.themeUtils.shouldDirectCheckout()) {
            e.preventDefault();
            e.stopPropagation();
            this.addToCartAndCheckout(variantId, btn, this.savedSingleBtnNodes, productHandle);
            return;
        }

        // Check action type
        if (this.config.single_action === 'direct_checkout') {
            e.preventDefault();
            e.stopPropagation();
            this.addToCartAndCheckout(variantId, btn, this.savedSingleBtnNodes, productHandle);
            return;
        }

        // Add to cart
        this.addToCart(variantId, btn, this.savedSingleBtnNodes, productHandle);
    },

    /**
     * Restore button content using saved nodes
     */
    restoreButtonContent(btn, savedNodes) {
        btn.textContent = '';
        savedNodes.forEach(function(node) {
            btn.appendChild(node.cloneNode(true));
        });
    },

    buildAnalyticsMeta(btn, variantId, productHandle, action) {
        const label = (btn.textContent || '').replace(/\s+/g, ' ').trim();
        return {
            cta_action: action,
            cta_text: label,
            cta_surface: 'upsell_popup',
            upsell_option: btn.hasAttribute('data-upsell-bundle') ? 'bundle' : 'single',
            product_handle: productHandle || '',
            variant_id: variantId
        };
    },

    /**
     * Add product to cart via Shopify AJAX API
     */
    async addToCart(variantId, btn, savedNodes, productHandle) {
        // Save button state
        const analyticsMeta = this.buildAnalyticsMeta(btn, variantId, productHandle, 'upsell_add_to_cart');
        btn.disabled = true;
        btn.textContent = _upsellT.checking || 'Checking...';
        if (window.themeAnalytics) {
            window.themeAnalytics.setCartIntent(analyticsMeta);
            window.themeAnalytics.capture('upsell_option_clicked', analyticsMeta);
        }

        try {
            await window.cartOperations.run(async () => {
            // Check if item can be added (no duplicates, not in bundle)
            if (window.Cart && window.Cart.canAddToCart) {
                const check = await window.Cart.canAddToCart(variantId, productHandle);
                if (!check.allowed) {
                    btn.textContent = check.reason;
                    setTimeout(() => {
                        btn.disabled = false;
                        this.restoreButtonContent(btn, savedNodes);
                    }, 2500);
                    return;
                }
            }

            btn.textContent = _upsellT.adding || 'Adding...';

            const response = await fetch(_upsellRootUrl + 'cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    items: [{ id: parseInt(variantId), quantity: 1 }]
                })
            });

            if (!response.ok) throw new Error('Failed to add to cart');

            var addData = await response.json();
            if (window.__publishAddToCart) window.__publishAddToCart(addData);
            // Fire-and-forget: no redirect follows, so ordering doesn't matter here.
            if (window.themeUtils && window.themeUtils.syncClickAttributes) window.themeUtils.syncClickAttributes();

            // Fetch fresh cart data before dispatching event (with cache bust)


            // Success - dispatch event and close popup
            this.close();
            document.dispatchEvent(new CustomEvent('cart:added'));
            document.dispatchEvent(new CustomEvent('upsell:added', {
                detail: { variantId: variantId }
            }));

            btn.textContent = _upsellT.added || 'Added!';

            // Close popup after brief delay
            setTimeout(() => {
                // Restore button after popup animation
                setTimeout(() => {
                    btn.disabled = false;
                    this.restoreButtonContent(btn, savedNodes);
                }, 300);
            }, 500);

            });
        } catch (err) {
            console.error('Upsell add to cart error:', err);
            if (window.themeAnalytics) {
                window.themeAnalytics.capture('upsell_cart_add_failed', Object.assign({}, analyticsMeta, {
                    error_message: String(err && err.message || err)
                }));
            }
            btn.disabled = false;
            this.restoreButtonContent(btn, savedNodes);
        }
    },

    /**
     * Add product to cart then navigate to checkout.
     * Popup direct-checkout flows use the standard checkout route instead of the
     * cart permalink shortcut so bundle upsells keep the selected line item.
     */
    async addToCartAndCheckout(variantId, btn, savedNodes, productHandle) {
        const analyticsMeta = this.buildAnalyticsMeta(btn, variantId, productHandle, 'upsell_direct_checkout');
        btn.disabled = true;
        btn.textContent = _upsellT.checking || 'Checking...';
        if (window.themeAnalytics) {
            window.themeAnalytics.setCartIntent(analyticsMeta);
            window.themeAnalytics.capture('upsell_option_clicked', analyticsMeta);
        }

        try {
            const result = await window.themeUtils.redirectToCheckout(variantId, analyticsMeta);
            if (!result || result.ok === false) throw new Error('Could not confirm cart before checkout');
        } catch (err) {
            console.error('Upsell direct checkout error:', err);
            if (window.themeAnalytics) {
                window.themeAnalytics.capture('upsell_cart_add_failed', Object.assign({}, analyticsMeta, {
                    error_message: String(err && err.message || err)
                }));
            }
            // Visible, retryable error instead of a silent button revert
            btn.textContent = _upsellT.checkoutError || "Couldn't start checkout — tap to retry";
            setTimeout(() => {
                btn.disabled = false;
                this.restoreButtonContent(btn, savedNodes);
            }, 2500);
        }
    }
};

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
    UpsellPopup.init();

    // Fire confetti if popup is already open (editor preview mode)
    if (UpsellPopup.popup && UpsellPopup.popup.classList.contains('open')) {
        setTimeout(() => UpsellPopup.fireConfetti(), 100);
    }
});

// Re-initialize on Shopify section load (theme editor)
document.addEventListener('shopify:section:load', (e) => {
    // Save scroll position before section re-render causes scroll
    const scrollY = window.scrollY;
    const scrollX = window.scrollX;

    // Clean up old event listeners before re-init
    UpsellPopup.destroyPopupEvents();
    UpsellPopup.init();

    // Fire confetti if the reloaded section has the popup open (editor preview toggle)
    if (UpsellPopup.popup && UpsellPopup.popup.classList.contains('open')) {
        // Restore scroll immediately and keep restoring to fight editor auto-scroll
        window.scrollTo(scrollX, scrollY);
        requestAnimationFrame(() => window.scrollTo(scrollX, scrollY));
        setTimeout(() => {
            window.scrollTo(scrollX, scrollY);
            UpsellPopup.fireConfetti();
        }, 100);
        setTimeout(() => window.scrollTo(scrollX, scrollY), 300);
    }
});

// Expose to global scope
window.UpsellPopup = UpsellPopup;
